import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusCircle, Trash2, UploadCloud, Image as ImageIcon, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { getApiUrl, getSupabaseHeaders } from '../../utils/api';
import { getAuthToken } from '../../utils/sessionAuth';
import { generateDescription } from '../../services/aiService';

const DashboardForm = ({ dashboard: dashboardToEdit, onSave: onFormSubmit, onCancel, isLoading }) => {
  const navigate = useNavigate();
  const { slug: paramSlug } = useParams(); // For edit mode via URL

  // Dynamic tool options from the API
  const [toolOptions, setToolOptions] = useState([]);

  const initialFormData = {
    title: '',
    slug: '',
    category: '',
    powerBiEmbedLink: '',
    about: '',
    mainImage: null,
    additionalImages: [],
    isFeatured: false,
    tags: [], // Kept for general tagging if needed
    toolsUsed: [], // New field for multi-select tools
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    createdDate: new Date().toISOString().split('T')[0], // Default to today's date
  };

  const [formData, setFormData] = useState(initialFormData);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState([]);
  const [additionalImagesPreviewTypes, setAdditionalImagesPreviewTypes] = useState([]); // Track if each preview is 'existing' or 'new'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [availableTools, setAvailableTools] = useState([]);
  const [newTool, setNewTool] = useState('');
  const [showAddTool, setShowAddTool] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState(null);
  const [metadataSuccess, setMetadataSuccess] = useState(null);

  // Fetch categories and tools when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(getApiUrl('dashboards/categories'), {
          method: 'GET',
          headers: {
            ...getSupabaseHeaders(),
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const responseData = await response.json();
          // Handle both direct response and wrapped response formats
          const categories = responseData.data?.categories || responseData.categories || [];
          setCategories(categories);
        } else {
          console.error('Failed to fetch categories - HTTP status:', response.status);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    
    const fetchTools = async () => {
      try {
        const response = await fetch(getApiUrl('dashboards/tools'), {
          method: 'GET',
          headers: getSupabaseHeaders()
        });
        if (response.ok) {
          const responseData = await response.json();
          // Handle both direct response and wrapped response formats
          const tools = responseData.data?.tools || responseData.tools || [];
          setAvailableTools(tools);
          // Convert tools to react-select format
          const toolOptionsFormatted = tools.map(tool => ({
            value: tool,
            label: tool
          }));
          setToolOptions(toolOptionsFormatted);
        } else {
          console.error('Failed to fetch tools - HTTP status:', response.status);
        }
      } catch (err) {
        console.error('Failed to fetch tools:', err);
      }
    };
    
    fetchCategories();
    fetchTools();
  }, []);

  useEffect(() => {
    if (dashboardToEdit) {
      setIsEditMode(true);
      
      // Format toolsUsed for react-select
      let formattedToolsUsed = [];
      if (dashboardToEdit.toolsUsed) {
        if (Array.isArray(dashboardToEdit.toolsUsed)) {
          formattedToolsUsed = dashboardToEdit.toolsUsed.map(tool => {
            if (typeof tool === 'string') {
              return { value: tool, label: tool };
            } else if (typeof tool === 'object' && tool !== null) {
              // If it's already in the correct format
              if (tool.value && tool.label) {
                return tool;
              }
              // If it's an object but not in the correct format
              const value = tool.name || tool.id || JSON.stringify(tool);
              return { value, label: value };
            }
            return null;
          }).filter(Boolean); // Remove any null values
        } else if (typeof dashboardToEdit.toolsUsed === 'string' && dashboardToEdit.toolsUsed) {
          // Handle case where toolsUsed might be a comma-separated string
          formattedToolsUsed = dashboardToEdit.toolsUsed.split(',').map(tool => ({
            value: tool.trim(),
            label: tool.trim()
          }));
        }
      }
      
      setFormData({
        ...initialFormData, // Start with defaults
        title: dashboardToEdit.title || '',
        slug: dashboardToEdit.slug || '',
        category: dashboardToEdit.category || '',
        toolsUsed: formattedToolsUsed,
        powerBiEmbedLink: dashboardToEdit.powerBIEmbedLink || '', // Fixed field name to match backend model
        about: dashboardToEdit.about || '',
        mainImage: null, // Don't pre-fill file inputs
        mainImageUrl: dashboardToEdit.mainImageUrl || dashboardToEdit.mainImage || '',
        additionalImages: [],
        existingAdditionalImageUrls: Array.isArray(dashboardToEdit.additionalImages) ? dashboardToEdit.additionalImages : [],
        metaTitle: dashboardToEdit.metaTitle || dashboardToEdit.meta_title || '',
        metaDescription: dashboardToEdit.metaDescription || dashboardToEdit.meta_description || '',
        metaKeywords: dashboardToEdit.metaKeywords || dashboardToEdit.meta_keywords || '',
        isFeatured: dashboardToEdit.isFeatured || false,
        createdDate: dashboardToEdit.createdDate ? new Date(dashboardToEdit.createdDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });
      
      // Set main image preview
      if (dashboardToEdit.mainImageUrl || dashboardToEdit.mainImage) {
        setMainImagePreview(dashboardToEdit.mainImageUrl || dashboardToEdit.mainImage);
      }
      
      // Set additional images preview
        if (dashboardToEdit.additionalImages && dashboardToEdit.additionalImages.length > 0) {
          setAdditionalImagesPreview(dashboardToEdit.additionalImages);
          setAdditionalImagesPreviewTypes(new Array(dashboardToEdit.additionalImages.length).fill('existing'));
      } else if (dashboardToEdit.additionalImages && dashboardToEdit.additionalImages.length > 0) {
        setAdditionalImagesPreview(
          dashboardToEdit.additionalImages.map(img => 
            typeof img === 'string' ? img : URL.createObjectURL(img)
          )
        );
        setAdditionalImagesPreviewTypes(new Array(dashboardToEdit.additionalImages.length).fill('existing'));
      }
    } else if (paramSlug) {
      // Fetch dashboard by slug if not passed as prop (e.g. direct navigation to edit page)
      // This part might need adjustment based on your API and routing
      const fetchDashboardBySlug = async () => {
        if (!paramSlug) {
          setError('Dashboard slug is required for editing.');
          setLoading(false);
          return;
        }
        
        setLoading(true);
        try {
          const response = await fetch(getApiUrl(`dashboards/${paramSlug}`));
          if (response.ok) {
            const data = await response.json();
            setIsEditMode(true);
            
            // Format toolsUsed for react-select
            let formattedToolsUsed = [];
            if (data.toolsUsed) {
              if (Array.isArray(data.toolsUsed)) {
                formattedToolsUsed = data.toolsUsed.map(tool => {
                  if (typeof tool === 'string') {
                    return { value: tool, label: tool };
                  } else if (typeof tool === 'object' && tool !== null) {
                    // If it's already in the correct format
                    if (tool.value && tool.label) {
                      return tool;
                    }
                    // If it's an object but not in the correct format
                    const value = tool.name || tool.id || JSON.stringify(tool);
                    return { value, label: value };
                  }
                  return null;
                }).filter(Boolean); // Remove any null values
              } else if (typeof data.toolsUsed === 'string' && data.toolsUsed) {
                // Handle case where toolsUsed might be a comma-separated string
                formattedToolsUsed = data.toolsUsed.split(',').map(tool => ({
                  value: tool.trim(),
                  label: tool.trim()
                }));
              }
            }
            
            setFormData({
              ...initialFormData,
              title: data.title || '',
              slug: data.slug || '',
              category: data.category || '',
              toolsUsed: formattedToolsUsed,
              powerBiEmbedLink: data.powerBIEmbedLink || '', // Fixed field name to match backend model
              about: data.about || '',
              mainImage: null,
              mainImageUrl: data.mainImageUrl || data.mainImage || '',
              additionalImages: [],
              existingAdditionalImageUrls: Array.isArray(data.additionalImages) ? data.additionalImages : [],
              metaTitle: data.metaTitle || data.meta_title || '',
              metaDescription: data.metaDescription || data.meta_description || '',
              metaKeywords: data.metaKeywords || data.meta_keywords || '',
              isFeatured: data.isFeatured || false,
            });
            
            // Set main image preview
            if (data.mainImageUrl || data.mainImage) {
              setMainImagePreview(data.mainImageUrl || data.mainImage);
            }
            
            // Set additional images preview - DON'T add existing images to additionalImages array
            if (data.additionalImages && data.additionalImages.length > 0) {
              setAdditionalImagesPreview(data.additionalImages);
              setAdditionalImagesPreviewTypes(new Array(data.additionalImages.length).fill('existing'));
              // Keep additionalImages empty for new files only, store existing in existingAdditionalImageUrls
              setFormData(prev => ({
                ...prev,
                additionalImages: [], // Keep empty for new files only
                existingAdditionalImageUrls: [...data.additionalImages]
              }));
            } else if (data.additionalImages && data.additionalImages.length > 0) {
              const existingImageUrls = data.additionalImages.filter(img => typeof img === 'string');
              setAdditionalImagesPreview(existingImageUrls);
              setAdditionalImagesPreviewTypes(new Array(existingImageUrls.length).fill('existing'));
              // Keep additionalImages empty for new files only
              setFormData(prev => ({ 
                ...prev, 
                additionalImages: [], // Keep empty for new files only
                existingAdditionalImageUrls: existingImageUrls
              }));
            }
          } else {
            setError('Failed to fetch dashboard details for editing.');
          }
        } catch (err) {
          const errorMsg = typeof err === 'string' ? err : 'Network error fetching dashboard for editing.';
          setError(errorMsg);
        } finally {
          setLoading(false);
        }
      };
      fetchDashboardBySlug();
    } else {
      setIsEditMode(false);
      setFormData(initialFormData);
      setMainImagePreview(null);
      setAdditionalImagesPreview([]);
    }
  }, [dashboardToEdit, paramSlug]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Update form data
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Auto-generate slug from title (always update slug when title changes)
      if (name === 'title') {
        const slug = value
          .toLowerCase()
          .replace(/[^\w\s-]/g, '') // Remove special characters
          .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
          .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
        
        updatedData.slug = slug;
      }
      
      return updatedData;
    });
  };
  
  const handleToolsUsedChange = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      toolsUsed: selectedOptions || [] // react-select might pass null if cleared
    }));
  };

  const handleAddCategory = () => {
    if (newCategory.trim() === '') return;
    
    // Check if category already exists
    if (!categories.includes(newCategory.trim())) {
      setCategories(prev => [...prev, newCategory.trim()]);
    }
    
    // Set the new category as the selected one
    setFormData(prev => ({
      ...prev,
      category: newCategory.trim()
    }));
    
    // Reset the new category input and hide the form
    setNewCategory('');
    setShowAddCategory(false);
  };

  const handleAddTool = () => {
    if (newTool.trim() && !availableTools.includes(newTool.trim())) {
      const updatedTools = [...availableTools, newTool.trim()];
      setAvailableTools(updatedTools);
      
      // Update tool options for react-select
      const newToolOption = { value: newTool.trim(), label: newTool.trim() };
      const updatedToolOptions = [...toolOptions, newToolOption];
      setToolOptions(updatedToolOptions);
      
      // Add the new tool to the current selection
      const updatedSelectedTools = [...formData.toolsUsed, newToolOption];
      setFormData(prev => ({ ...prev, toolsUsed: updatedSelectedTools }));
      
      setNewTool('');
      setShowAddTool(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, mainImage: file }));
      setMainImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setFormData(prev => ({ ...prev, additionalImages: [...prev.additionalImages, ...files] }));
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setAdditionalImagesPreview(prev => [...prev, ...newPreviews]);
      setAdditionalImagesPreviewTypes(prev => [...prev, ...new Array(files.length).fill('new')]);
    }
  };

  const handleRemoveAdditionalImage = (indexToRemove) => {
    const imageType = additionalImagesPreviewTypes[indexToRemove];
    const imageToRemove = additionalImagesPreview[indexToRemove];
    
    // Remove from preview and types arrays
    setAdditionalImagesPreview(prev => prev.filter((_, index) => index !== indexToRemove));
    setAdditionalImagesPreviewTypes(prev => prev.filter((_, index) => index !== indexToRemove));
    
    if (imageType === 'existing') {
      // Remove from existingAdditionalImageUrls
      setFormData(prev => ({
        ...prev,
        existingAdditionalImageUrls: prev.existingAdditionalImageUrls.filter(url => url !== imageToRemove)
      }));
    } else {
      // Remove from additionalImages (new files) - need to find the correct index
      // Count how many 'existing' images come before this index
      const existingImagesBeforeIndex = additionalImagesPreviewTypes.slice(0, indexToRemove).filter(type => type === 'existing').length;
      const newFileIndex = indexToRemove - existingImagesBeforeIndex;
      
      setFormData(prev => ({
        ...prev,
        additionalImages: prev.additionalImages.filter((_, index) => index !== newFileIndex)
      }));
    }
  };

  const handleGenerateDescription = async () => {
    // Check if required fields are filled
    if (!formData.title) {
      setDescriptionError('Please fill in the Title field before generating description.');
      return;
    }

    setIsGeneratingDescription(true);
    setDescriptionError(null);

    try {
      const result = await generateDescription({
        title: formData.title,
        category: formData.category,
        toolsUsed: formData.toolsUsed?.map(tool => tool.value || tool) || [],
        existingDescription: formData.about || undefined
      });

      if (result.success) {
        // Update form data with generated description
        setFormData(prev => ({
          ...prev,
          about: result.description
        }));
        
        // Show success message with source info
        const sourceText = result.source === 'ai' ? 'AI-generated' : 'Template-based';
        setSuccess(`Description generated successfully! (${sourceText})`);
      } else {
        setDescriptionError('Failed to generate description. Please try again.');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      setDescriptionError(error.message || 'Failed to generate description. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
      
      // Clear success/error messages after 5 seconds
      setTimeout(() => {
        setSuccess(null);
        setDescriptionError(null);
      }, 5000);
    }
  };

  const handleGenerateMetadata = async () => {
    // Check if required fields are filled
    if (!formData.title || !formData.category || !formData.about) {
      setMetadataError('Please fill in Title, Category, and About fields before generating metadata.');
      return;
    }

    setIsGeneratingMetadata(true);
    setMetadataError(null);
    setMetadataSuccess(null);

    try {
      const token = getAuthToken();
      
      if (!token) {
        setMetadataError('Authentication token not found. Please log in again.');
        return;
      }

      // Prepare form data for the API call
      const metadataFormData = new FormData();
      metadataFormData.append('title', formData.title);
      metadataFormData.append('category', formData.category);
      metadataFormData.append('about', formData.about);
      
      // Add tools used
      if (formData.toolsUsed && formData.toolsUsed.length > 0) {
        formData.toolsUsed.forEach(tool => {
          metadataFormData.append('tools_used', tool.value || tool);
        });
      }

      // Import getSupabaseHeaders for Supabase function calls
      const { getSupabaseHeaders } = await import('../../utils/api');
      
      // Create headers without Content-Type for FormData (browser will set it automatically)
      let headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // For Supabase functions, we need to include the required headers
      if ((import.meta.env.VITE_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL) && (import.meta.env.VITE_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL).includes('supabase.co')) {
        // Skip Content-Type for FormData - let browser set it automatically
        const supabaseHeaders = getSupabaseHeaders(true);
        headers = {
          ...supabaseHeaders,
          'Authorization': `Bearer ${token}` // Override with session token
        };
      }

      const response = await fetch(getApiUrl('dashboards/generate-metadata'), {
        method: 'POST',
        headers: headers,
        body: metadataFormData
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.metadata) {
          // Update form data with generated metadata
          setFormData(prev => ({
            ...prev,
            metaTitle: result.metadata.metaTitle || '',
            metaDescription: result.metadata.metaDescription || '',
            metaKeywords: result.metadata.metaKeywords || ''
          }));
          
          setMetadataSuccess('Metadata generated successfully!');
          
          if (result.note) {
            setMetadataSuccess(`Metadata generated successfully! Note: ${result.note}`);
          }
        } else {
          setMetadataError('Failed to generate metadata. Please try again.');
        }
      } else {
        const errorData = await response.json();
        setMetadataError(errorData.detail || 'Failed to generate metadata. Please try again.');
      }
    } catch (error) {
      console.error('Error generating metadata:', error);
      setMetadataError('Network error. Please check your connection and try again.');
    } finally {
      setIsGeneratingMetadata(false);
      
      // Clear success/error messages after 5 seconds
      setTimeout(() => {
        setMetadataSuccess(null);
        setMetadataError(null);
      }, 5000);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStage('Preparing submission...');

    // Declare interval variables for cleanup
    let progressInterval, uploadInterval, finalInterval;

    try {
      // For updates, we need to use the dashboard ID, not the slug
      // The backend only supports PUT with /{dashboard_id}
      const dashboardId = dashboardToEdit?.id || dashboardToEdit?._id;
      
      // Validate dashboardId for edit mode
      if (isEditMode && !dashboardId) {
        setError('Dashboard ID is required for editing. Please try again.');
        return;
      }
      
      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode && dashboardId
        ? getApiUrl(`dashboards/${dashboardId}`, method)
        : getApiUrl('dashboards', method);

      // Get the authentication token from session
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      let requestData;
      let headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Import getSupabaseHeaders for Supabase function calls
      const { getSupabaseHeaders } = await import('../../utils/api');
      
      // For Supabase functions, we need to include the required headers
      if ((import.meta.env.VITE_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL) && (import.meta.env.VITE_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL).includes('supabase.co')) {
        // Skip Content-Type for FormData - let browser set it automatically
        const supabaseHeaders = getSupabaseHeaders(true);
        headers = {
          ...supabaseHeaders,
          'Authorization': `Bearer ${token}` // Override with session token
        };
      }

      // Use FormData for both create and update to handle file uploads
      setUploadStage('Preparing form data...');
      
      // Start smooth progress animation
      let currentProgress = 0;
      progressInterval = setInterval(() => {
        currentProgress += Math.random() * 3 + 1; // Random increment between 1-4%
        if (currentProgress < 25) {
          setUploadProgress(Math.round(Math.min(currentProgress, 25)));
        }
      }, 100);
      
      const data = new FormData();
      
      // Append all form fields to FormData with correct field names matching backend's snake_case
      data.append('title', formData.title);
      data.append('category', formData.category || '');
      data.append('power_bi_embed_link', formData.powerBiEmbedLink); // Match backend field name
      data.append('about', formData.about || '');
      data.append('slug', formData.slug || '');
      data.append('is_featured', formData.isFeatured);
      data.append('created_date', formData.createdDate);
      
      // Handle metadata fields
      data.append('meta_title', formData.metaTitle || '');
      data.append('meta_description', formData.metaDescription || '');
      data.append('meta_keywords', formData.metaKeywords || '');
      
      // Handle toolsUsed array - send as individual form fields
      if (formData.toolsUsed && formData.toolsUsed.length > 0) {
        formData.toolsUsed.forEach(tool => {
          data.append('tools_used', tool.value || tool);
        });
      }
      
      setUploadStage('Preparing images...');
      // Progress continues smoothly in background
      
      // Handle file uploads
      if (formData.mainImage && formData.mainImage instanceof File) {
        data.append('main_image', formData.mainImage);
      }
      
      // Handle additional images - separate new files from existing URLs
      const existingImageUrls = new Set(); // Use Set to avoid duplicates
      const newImageFiles = [];
      
      // First, add existing image URLs from the original dashboard that are still present
      if (isEditMode && formData.existingAdditionalImageUrls) {
        formData.existingAdditionalImageUrls.forEach(url => existingImageUrls.add(url));
      }
      
      // Then process current additionalImages array (should only contain new File objects now)
      formData.additionalImages.forEach(item => {
        if (item instanceof File) {
          newImageFiles.push(item);
        } else if (typeof item === 'string') {
          // This shouldn't happen anymore, but keep for safety
          console.warn('Found string in additionalImages array:', item);
          existingImageUrls.add(item);
        }
      });
      
      console.log('New image files to upload:', newImageFiles.length);
      console.log('Existing image URLs to preserve:', Array.from(existingImageUrls));
      
      // Convert Set back to Array and send existing image URLs
      const existingImageUrlsArray = Array.from(existingImageUrls);
      if (existingImageUrlsArray.length > 0) {
        data.append('existing_additional_images', JSON.stringify(existingImageUrlsArray));
        console.log('Sending existing_additional_images:', existingImageUrlsArray);
      }
      
      // Send new image files
      newImageFiles.forEach(file => {
        data.append('additional_images', file);
      });
      
      requestData = data;
      // Don't set Content-Type for FormData - let browser set it

      setUploadStage(`${isEditMode ? 'Updating' : 'Creating'} dashboard...`);
      
      // Update progress to 50% range
      currentProgress = 25;
      uploadInterval = setInterval(() => {
        currentProgress += Math.random() * 2 + 0.5; // Slower increment during upload
        if (currentProgress < 70) {
          setUploadProgress(Math.round(Math.min(currentProgress, 70)));
        }
      }, 150);

      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: requestData
      });

      clearInterval(progressInterval);
      clearInterval(uploadInterval);
      
      setUploadStage('Processing response...');
      
      const responseData = await response.json();

      // Final progress animation - ensure it completes to 100%
      currentProgress = 70;
      setUploadStage('Finalizing...');
      
      finalInterval = setInterval(() => {
        currentProgress += Math.random() * 4 + 2;
        if (currentProgress < 99) {
          setUploadProgress(Math.round(Math.min(currentProgress, 99)));
        } else {
          clearInterval(finalInterval);
          setUploadProgress(100);
          
          // Process the response after reaching 100%
           setTimeout(() => {
             if (response.ok) {
               setSuccess(`Dashboard ${isEditMode ? 'updated' : 'created'} successfully!`);
               if (onFormSubmit) {
                 onFormSubmit(responseData, dashboardToEdit?.id || responseData.data?.id);
               }
               if (!isEditMode) {
                 setFormData(initialFormData);
                 setMainImagePreview(null);
                 setAdditionalImagesPreview([]);
               }
             } else {
               // Handle error case
               handleResponseError(responseData);
             }
             
             // Clean up after showing the result
             setTimeout(() => {
               setLoading(false);
               setIsUploading(false);
               setUploadProgress(0);
               setUploadStage('');
             }, 1000);
           }, 800); // 800ms delay to show completion animation
        }
      }, 80);
      
      // Helper function to handle errors
       const handleResponseError = (responseData) => {
         // Handle different error response formats
         let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} dashboard`;
         
         if (responseData.detail) {
           // Check if detail is an array of validation errors
           if (Array.isArray(responseData.detail)) {
             // Extract messages from validation errors
             const errorMessages = responseData.detail.map(err => {
               if (typeof err === 'object' && err.msg) {
                 return `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`;
               }
               return typeof err === 'string' ? err : JSON.stringify(err);
             });
             errorMessage = errorMessages.join(', ');
           } else if (typeof responseData.detail === 'string') {
             errorMessage = responseData.detail;
           } else {
             errorMessage = JSON.stringify(responseData.detail);
           }
         } else if (responseData.message) {
           errorMessage = responseData.message;
         } else if (responseData.error) {
           errorMessage = responseData.error;
         }
         
         console.error('API Error:', responseData);
         // Ensure error is always a string
         const finalError = typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage);
         setError(finalError);
       };
    } catch (err) {
      // Clear any remaining intervals
      clearInterval(progressInterval);
      clearInterval(uploadInterval);
      clearInterval(finalInterval);
      
      console.error('Error submitting form:', err);
      const errorMsg = `An error occurred while ${isEditMode ? 'updating' : 'creating'} the dashboard.`;
      setError(errorMsg);
      
      // Reset UI state immediately on error
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStage('');
    } finally {
      // Cleanup is now handled within the progress completion flow
      // Only reset on error cases
      if (!isUploading) {
        setLoading(false);
      }
    }
  };

  const inputClass = "w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500";
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5";

  // Custom styles for react-select to match dark/light theme
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'var(--select-bg-color)',
      borderColor: state.isFocused ? 'var(--select-border-focus-color)' : 'var(--select-border-color)',
      boxShadow: state.isFocused ? '0 0 0 1px var(--select-border-focus-color)' : 'none',
      '&:hover': {
        borderColor: 'var(--select-border-hover-color)',
      },
      minHeight: '42px', // Match other inputs
      borderRadius: '0.5rem', // Match other inputs (lg)
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--select-menu-bg-color)',
      zIndex: 50, // Ensure dropdown is on top
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'var(--select-option-selected-bg-color)' : (state.isFocused ? 'var(--select-option-hover-bg-color)' : 'var(--select-menu-bg-color)'),
      color: state.isSelected ? 'var(--select-option-selected-text-color)' : 'var(--select-option-text-color)',
      '&:active': {
        backgroundColor: 'var(--select-option-active-bg-color)',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'var(--select-multi-value-bg-color)',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'var(--select-multi-value-label-color)',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'var(--select-multi-value-remove-color)',
      '&:hover': {
        backgroundColor: 'var(--select-multi-value-remove-hover-bg-color)',
        color: 'var(--select-multi-value-remove-hover-color)',
      },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: 'var(--select-placeholder-color)',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: 'var(--select-value-color)',
    }),
    input: (provided) => ({
        ...provided,
        color: 'var(--select-input-color)',
    }),
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-5xl mx-auto pb-24 sm:pb-12">
      <style>
        {`
          :root {
            --select-bg-color: #ffffff;
            --select-border-color: #d1d5db; /* gray-300 */
            --select-border-focus-color: #3b82f6; /* blue-500 */
            --select-border-hover-color: #9ca3af; /* gray-400 */
            --select-menu-bg-color: #ffffff;
            --select-option-selected-bg-color: #2563eb; /* blue-600 */
            --select-option-selected-text-color: #ffffff;
            --select-option-hover-bg-color: #eff6ff; /* blue-50 */
            --select-option-text-color: #1f2937; /* gray-800 */
            --select-option-active-bg-color: #dbeafe; /* blue-100 */
            --select-multi-value-bg-color: #e0e7ff; /* indigo-100 */
            --select-multi-value-label-color: #3730a3; /* indigo-800 */
            --select-multi-value-remove-color: #4338ca; /* indigo-700 */
            --select-multi-value-remove-hover-bg-color: #c7d2fe; /* indigo-200 */
            --select-multi-value-remove-hover-color: #312e81; /* indigo-900 */
            --select-placeholder-color: #9ca3af; /* gray-400 */
            --select-value-color: #1f2937; /* gray-800 */
            --select-input-color: #1f2937; /* gray-800 */
          }
          .dark {
            --select-bg-color: #374151; /* gray-700 */
            --select-border-color: #4b5563; /* gray-600 */
            --select-border-focus-color: #60a5fa; /* blue-400 */
            --select-border-hover-color: #6b7280; /* gray-500 */
            --select-menu-bg-color: #374151; /* gray-700 */
            --select-option-selected-bg-color: #3b82f6; /* blue-500 */
            --select-option-selected-text-color: #ffffff;
            --select-option-hover-bg-color: #4b5563; /* gray-600 */
            --select-option-text-color: #e5e7eb; /* gray-200 */
            --select-option-active-bg-color: #525f76; /* adjusted */
            --select-multi-value-bg-color: #4338ca; /* indigo-700 */
            --select-multi-value-label-color: #e0e7ff; /* indigo-100 */
            --select-multi-value-remove-color: #c7d2fe; /* indigo-200 */
            --select-multi-value-remove-hover-bg-color: #5a67d8; /* indigo-500 */
            --select-multi-value-remove-hover-color: #ffffff;
            --select-placeholder-color: #9ca3af; /* gray-400 */
            --select-value-color: #e5e7eb; /* gray-200 */
            --select-input-color: #e5e7eb; /* gray-200 */
          }
        `}
      </style>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        {isEditMode ? 'Edit Dashboard' : 'Create New Dashboard'}
      </h2>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex items-center mb-6">
          <AlertTriangle className="mr-2 h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg flex items-center mb-6">
          <CheckCircle className="mr-2 h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Progress Bar */}
      {isUploading && (
        <div className="mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 transform transition-all duration-300 shadow-lg animate-fadeIn">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-all duration-300">
              <span className={uploadProgress > 0 && uploadProgress < 100 ? 'animate-pulse' : ''}>
                {uploadStage}
              </span>
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400 font-mono transition-all duration-300">
              {uploadProgress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 h-2.5 rounded-full transition-all duration-500 ease-out relative"
              style={{ 
                width: `${uploadProgress}%`,
                backgroundSize: '200% 100%',
                animation: uploadProgress === 100 ? 'progressComplete 0.8s ease-out' : uploadProgress > 0 && uploadProgress < 100 ? 'progressPulse 2s ease-in-out infinite, progressShine 3s linear infinite' : 'none'
              }}
            ></div>
          </div>
          <style jsx>{`
             @keyframes progressPulse {
               0%, 100% {
                 opacity: 0.8;
                 transform: scaleY(1);
               }
               50% {
                 opacity: 1;
                 transform: scaleY(1.1);
               }
             }
             @keyframes progressShine {
               0% {
                 background-position: 200% 0;
               }
               100% {
                 background-position: -200% 0;
               }
             }
             @keyframes progressComplete {
               0% {
                 background: linear-gradient(to right, #3b82f6, #2563eb, #3b82f6);
                 transform: scaleY(1);
                 box-shadow: 0 0 0 rgba(59, 130, 246, 0);
               }
               50% {
                 background: linear-gradient(to right, #10b981, #059669, #10b981);
                 transform: scaleY(1.2);
                 box-shadow: 0 0 20px rgba(16, 185, 129, 0.5);
               }
               100% {
                 background: linear-gradient(to right, #10b981, #059669, #10b981);
                 transform: scaleY(1);
                 box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
               }
             }
             @keyframes fadeIn {
               from {
                 opacity: 0;
                 transform: translateY(-10px);
               }
               to {
                 opacity: 1;
                 transform: translateY(0);
               }
             }
             .animate-fadeIn {
               animation: fadeIn 0.5s ease-out;
             }
          `}</style>
        </div>
      )}

      {/* Form Fields - Responsive Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <label htmlFor="title" className={labelClass}>Title <span className="text-red-500">*</span></label>
            <input type="text" name="title" id="title" value={formData.title} onChange={handleChange} className={inputClass} required placeholder="Dashboard title" />
          </div>
          
          <div>
            <label htmlFor="slug" className={labelClass}>Slug <span className="text-red-500">*</span></label>
            <input 
              type="text" 
              name="slug" 
              id="slug" 
              value={formData.slug} 
              onChange={handleChange} 
              className={`${inputClass} bg-gray-100 dark:bg-gray-800 cursor-not-allowed`} 
              required 
              placeholder="dashboard-url-slug" 
              disabled 
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">URL-friendly version of the title (auto-generated)</p>
          </div>
          
          <div>
            <label htmlFor="createdDate" className={labelClass}>Created Date <span className="text-red-500">*</span></label>
            <input 
              type="date" 
              name="createdDate" 
              id="createdDate" 
              value={formData.createdDate} 
              onChange={handleChange} 
              className={inputClass} 
              required 
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">The date when this dashboard was created</p>
          </div>
          
          <div>
            <label htmlFor="category" className={labelClass}>Category <span className="text-red-500">*</span></label>
            <div className="relative">
              {showAddCategory ? (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input 
                    type="text" 
                    value={newCategory} 
                    onChange={(e) => setNewCategory(e.target.value)} 
                    className={inputClass} 
                    placeholder="Enter new category" 
                    autoFocus 
                  />
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={handleAddCategory} 
                      className="flex-1 sm:flex-none px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      Add
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowAddCategory(false)} 
                      className="flex-1 sm:flex-none px-3 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <select 
                    name="category" 
                    id="category" 
                    value={formData.category} 
                    onChange={handleChange} 
                    className={inputClass} 
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <button 
                    type="button" 
                    onClick={() => setShowAddCategory(true)} 
                    className="w-full sm:w-auto px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                  >
                    New
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="powerBiEmbedLink" className={labelClass}>Power BI Embed Link <span className="text-red-500">*</span></label>
            <input type="url" name="powerBiEmbedLink" id="powerBiEmbedLink" value={formData.powerBiEmbedLink} onChange={handleChange} className={inputClass} required placeholder="https://app.powerbi.com/reportEmbed?..." />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isFeatured"
              id="isFeatured"
              checked={formData.isFeatured}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700 dark:checked:bg-blue-500"
            />
            <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
              Feature this dashboard?
            </label>
          </div>
        </div>

        {/* Middle Column */}
        <div className="space-y-4">
          {/* Tools Used - Multi-select */} 
          <div>
            <label htmlFor="toolsUsed" className={labelClass}>Tools Used</label>
            <div className="space-y-2">
              <Select
                id="toolsUsed"
                name="toolsUsed"
                components={makeAnimated()}
                isMulti
                options={toolOptions}
                classNamePrefix="react-select"
                value={formData.toolsUsed}
                onChange={handleToolsUsedChange}
                placeholder="Select or type to add tools..."
                isClearable
                isSearchable
                styles={selectStyles}
              />
              {showAddTool ? (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input 
                    type="text" 
                    value={newTool} 
                    onChange={(e) => setNewTool(e.target.value)} 
                    className={inputClass} 
                    placeholder="Enter new tool name" 
                    autoFocus 
                  />
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={handleAddTool} 
                      className="flex-1 sm:flex-none px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      Add
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowAddTool(false)} 
                      className="flex-1 sm:flex-none px-3 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  type="button" 
                  onClick={() => setShowAddTool(true)} 
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                >
                  Add New Tool
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Select multiple tools or technologies used in this dashboard</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="about" className={labelClass}>About / Description <span className="text-red-500">*</span></label>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={isGeneratingDescription || !formData.title}
                className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2"
                title={!formData.title ? 'Please enter a title first' : 'Generate AI description using OpenRouter DeepSeek'}
              >
                {isGeneratingDescription ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI Generate
                  </>
                )}
              </button>
            </div>
            <textarea name="about" id="about" rows="6" value={formData.about} onChange={handleChange} className={inputClass} required placeholder="Detailed description of the dashboard..."></textarea>
            {descriptionError && (
              <div className="mt-2 flex items-center text-red-600 text-sm">
                <XCircle className="w-4 h-4 mr-1" />
                {descriptionError}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Provide a detailed description of what this dashboard does and the insights it provides. 
              <span className="text-purple-600">Use AI Generate to create a professional description automatically.</span>
            </p>
          </div>

          {/* Placeholder for removed SEO fields - they will be moved to the bottom */}
        </div>

        {/* Right Column - Images */}
        <div className="space-y-4">
          {/* Main Image */} 
          <div>
            <label htmlFor="mainImage" className={labelClass}>Main Image <span className="text-red-500">*</span></label>
            <div className="mt-1 flex items-center">
              {mainImagePreview && (
                <div className="relative mr-4">
                  <img src={mainImagePreview} alt="Main preview" className="h-20 w-auto rounded-md object-cover" />
                  <button 
                    type="button" 
                    onClick={() => { 
                      setMainImagePreview(null); 
                      setFormData(p => ({...p, mainImage: null, mainImageUrl: null})); 
                    }} 
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                  >
                    <XCircle size={16}/>
                  </button>
                </div>
              )}
              {(!isEditMode || !mainImagePreview) && (
                <label className={`flex flex-col items-center justify-center w-full h-20 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 ${mainImagePreview ? 'w-auto px-4' : ''}`}>
                  <div className="flex flex-col items-center justify-center pt-2 pb-2">
                    <UploadCloud className="w-6 h-6 mb-1 text-gray-500 dark:text-gray-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span></p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">SVG, PNG, JPG or GIF</p>
                  </div>
                  <input id="mainImage" name="mainImage" type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
                </label>
              )}
              {isEditMode && mainImagePreview && (
                <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">
                  To change the main image, first remove the current one by clicking the X button.
                </p>
              )}
            </div>
          </div>

          {/* Additional Images */}
          <div>
            <label className={labelClass}>Additional Images (Optional)</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {additionalImagesPreview.map((preview, index) => (
                <div key={index} className="relative group">
                  <img src={preview} alt={`Additional preview ${index + 1}`} className="h-16 w-full rounded-md object-cover" />
                  <button 
                    type="button" 
                    onClick={() => handleRemoveAdditionalImage(index)}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <label className="flex flex-col items-center justify-center w-full h-16 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                <div className="flex flex-col items-center justify-center pt-2 pb-2">
                  <PlusCircle className="w-6 h-6 mb-1 text-gray-500 dark:text-gray-400" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Add images</p>
                </div>
                <input type="file" multiple className="hidden" onChange={handleAdditionalImagesChange} accept="image/*" />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* SEO & Meta Information - Collapsible Section */}
      <details className="mt-6 mb-4 border border-gray-200 dark:border-gray-700 rounded-lg">
        <summary className="px-4 py-3 bg-gray-50 dark:bg-gray-800 cursor-pointer font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <span>SEO & Meta Information</span>
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Click to expand)</span>
        </summary>
        <div className="p-4">
          {/* AI Metadata Generation Section */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">
                  🤖 AI-Powered Metadata Generation
                </h4>
                <p className="text-xs text-blue-600 dark:text-blue-300">
                  Generate SEO-optimized metadata automatically using AI based on your dashboard content.
                </p>
                <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                  Required: Title, Category, and About fields must be filled.
                </p>
              </div>
              <button
                type="button"
                onClick={handleGenerateMetadata}
                disabled={isGeneratingMetadata || !formData.title || !formData.category || !formData.about}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-lg transition-all duration-200 font-medium flex items-center gap-2 shadow-md hover:shadow-lg disabled:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isGeneratingMetadata ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Generate Metadata
                  </>
                )}
              </button>
            </div>
            
            {/* Status Messages */}
            {metadataError && (
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
                <div className="flex items-center">
                  <XCircle className="w-4 h-4 text-red-500 mr-2" />
                  <p className="text-sm text-red-700 dark:text-red-300">{metadataError}</p>
                </div>
              </div>
            )}
            
            {metadataSuccess && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <p className="text-sm text-green-700 dark:text-green-300">{metadataSuccess}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Metadata Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* SEO Meta Title */}
          <div>
            <label htmlFor="metaTitle" className={labelClass}>Meta Title</label>
            <input type="text" name="metaTitle" id="metaTitle" value={formData.metaTitle} onChange={handleChange} className={inputClass} placeholder="Concise and keyword-rich title for search engines" />
          </div>

          {/* SEO Meta Description */}
          <div>
            <label htmlFor="metaDescription" className={labelClass}>Meta Description</label>
            <textarea name="metaDescription" id="metaDescription" rows="3" value={formData.metaDescription} onChange={handleChange} className={inputClass} placeholder="Brief summary for search results (approx. 160 characters)"></textarea>
          </div>

          {/* SEO Meta Keywords */}
          <div>
            <label htmlFor="metaKeywords" className={labelClass}>Meta Keywords (comma-separated)</label>
            <input type="text" name="metaKeywords" id="metaKeywords" value={formData.metaKeywords} onChange={handleChange} className={inputClass} placeholder="e.g., sales dashboard, kpi tracking, data visualization" />
          </div>
          </div>
        </div>
      </details>

      {/* Actions */} 
      <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4 mb-20 sm:mb-12">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 text-gray-700 dark:text-gray-300 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-500 rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md border border-gray-300 dark:border-gray-600"
          >
            Cancel
          </button>
        )}
        <button
               type="submit"
               disabled={isLoading || loading}
               className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-blue-400 disabled:to-blue-500 text-white rounded-xl transition-all duration-200 font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
             >
             {(isLoading || loading) ? (
               <>
                 <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                 {isEditMode ? 'Updating...' : 'Creating...'}
               </>
             ) : (
               <>
                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                 </svg>
                 {isEditMode ? 'Update Dashboard' : 'Create Dashboard'}
               </>
             )}
           </button>
      </div>
      </form>
    </div>
  );
};

export default DashboardForm;